import { supabase } from '@/integrations/supabase/client';
import type { AuctionMetrics, DonationMetrics, TicketMetrics, TableCompetitionMetrics, EventTablesMetrics } from '@/types/dashboard';

export const getAuctionMetrics = async (
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<AuctionMetrics> => {
  // Get auction items data
  const { data: items, error: itemsError } = await supabase
    .from('auction_items')
    .select('estimated_value, current_bid, payment_status, payment_amount')
    .eq('organization_id', organizationId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (itemsError) throw itemsError;

  // Get bids count
  const { count: bidsCount, error: bidsError } = await supabase
    .from('auction_bids')
    .select('*', { count: 'exact', head: true })
    .eq('organization_id', organizationId)
    .gte('bid_timestamp', startDate.toISOString())
    .lte('bid_timestamp', endDate.toISOString());

  if (bidsError) throw bidsError;

  const totalEstimatedValue = items?.reduce((sum, item) => sum + Number(item.estimated_value || 0), 0) || 0;
  const paidPurchases = items?.filter(item => item.payment_status === 'paid').reduce((sum, item) => sum + Number(item.payment_amount || 0), 0) || 0;
  const pendingPurchases = items?.filter(item => item.payment_status === 'pending').reduce((sum, item) => sum + Number(item.payment_amount || 0), 0) || 0;

  return {
    totalEstimatedValue,
    totalItems: items?.length || 0,
    totalBids: bidsCount || 0,
    paidPurchases,
    pendingPurchases,
  };
};

export const getDonationMetrics = async (
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<DonationMetrics> => {
  const { data: gifts, error } = await supabase
    .from('gifts')
    .select('amount, payment_status, gift_category_id')
    .eq('organization_id', organizationId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString())
    .is('gift_category_id', null); // Regular donations without specific category

  if (error) throw error;

  const count = gifts?.length || 0;
  const total = gifts?.reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const paid = gifts?.filter(g => g.payment_status === 'paid').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const pending = gifts?.filter(g => g.payment_status === 'pending').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const average = count > 0 ? total / count : 0;

  return { count, average, paid, pending, total };
};

export const getTicketMetrics = async (
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<TicketMetrics> => {
  // Get gift categories to find ticket category
  const { data: categories } = await supabase
    .from('gift_categories')
    .select('id')
    .or('label.ilike.%ticket%,label.ilike.%admission%')
    .limit(1)
    .single();

  if (!categories) {
    return { count: 0, paid: 0, pending: 0, total: 0 };
  }

  const { data: gifts, error } = await supabase
    .from('gifts')
    .select('amount, payment_status')
    .eq('organization_id', organizationId)
    .eq('gift_category_id', categories.id)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (error) throw error;

  const count = gifts?.length || 0;
  const total = gifts?.reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const paid = gifts?.filter(g => g.payment_status === 'paid').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const pending = gifts?.filter(g => g.payment_status === 'pending').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;

  return { count, paid, pending, total };
};

export const getTableCompetitionMetrics = async (
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<TableCompetitionMetrics> => {
  // Get gifts associated with tables (table competition donations)
  const { data: gifts, error } = await supabase
    .from('gifts')
    .select('amount, payment_status, table_id')
    .eq('organization_id', organizationId)
    .not('table_id', 'is', null)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  if (error) throw error;

  const count = gifts?.length || 0;
  const total = gifts?.reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const paid = gifts?.filter(g => g.payment_status === 'paid').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const pending = gifts?.filter(g => g.payment_status === 'pending').reduce((sum, gift) => sum + Number(gift.amount || 0), 0) || 0;
  const average = count > 0 ? total / count : 0;

  return { count, average, paid, pending, total };
};

export const getEventTablesMetrics = async (
  organizationId: string,
  startDate: Date,
  endDate: Date
): Promise<EventTablesMetrics> => {
  // Get events in date range first
  const { data: events } = await supabase
    .from('events')
    .select('id')
    .eq('organization_id', organizationId)
    .gte('created_at', startDate.toISOString())
    .lte('created_at', endDate.toISOString());

  const eventIds = events?.map(e => e.id) || [];

  if (eventIds.length === 0) {
    return {
      totalTables: 0,
      soldTables: 0,
      availableTables: 0,
      reservedTables: 0,
      paidRevenue: 0,
      pendingRevenue: 0,
      totalRevenue: 0,
    };
  }

  // Get event tables data
  const { data: tables, error } = await supabase
    .from('event_tables')
    .select('status, price_per_seat, sold_seats, payment_status')
    .in('event_id', eventIds);

  if (error) throw error;

  const totalTables = tables?.length || 0;
  const soldTables = tables?.filter(t => t.status === 'sold').length || 0;
  const availableTables = tables?.filter(t => t.status === 'available').length || 0;
  const reservedTables = tables?.filter(t => t.status === 'reserved').length || 0;

  const paidRevenue = tables?.filter(t => t.payment_status === 'paid').reduce((sum, table) => {
    return sum + (Number(table.price_per_seat || 0) * Number(table.sold_seats || 0));
  }, 0) || 0;

  const pendingRevenue = tables?.filter(t => t.payment_status === 'pending').reduce((sum, table) => {
    return sum + (Number(table.price_per_seat || 0) * Number(table.sold_seats || 0));
  }, 0) || 0;

  const totalRevenue = paidRevenue + pendingRevenue;

  return {
    totalTables,
    soldTables,
    availableTables,
    reservedTables,
    paidRevenue,
    pendingRevenue,
    totalRevenue,
  };
};
